<template>
   <RequestLoader :isLoading="state.isFetchingData" data-cy="profile-sex-block" style="min-height: 13rem">
      <SelectChoiceGroup :choices="choices" group="profile-sex-choice" @choice-updated="updateChoices" />
      <InvalidInputsList :errorList="errorList" :class="{ hidden: !showBlockErrors }" />
   </RequestLoader>
</template>

<script setup lang="ts">
import { useI18n } from "@/vue-i18n/vue-i18n";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { AnswerResp } from "@/data/api-layer/answersApi";
import { useUserStore } from "@/stores/userStore";

export interface ProfileSexAnswerData {
   sex: string;
}
interface SexChoice {
   id: string;
   label: string;
   isSelected: boolean;
}
export interface ProfileSexBlock extends FlowselBlock {
   data: {
      choices: SexChoice[];
   };
}
interface Props {
   block: ProfileSexBlock;
   userBlockAnswer: AnswerResp | null;
   showBlockErrors: boolean;
}

// declarations
// -----------------------------------------
const { t: $t } = useI18n();
const userStore = useUserStore();
const emit = defineEmits(["update-answer", "update-valid"]);
const props = defineProps<Props>();

const state = reactive({
   isFetchingData: false,
   sex: null,
});

// computed
// -----------------------------------------
const choices = computed(() => {
   return props.block.data.choices.map((choice) => {
      return {
         ...choice,
         isSelected: state.sex === choice.id ? true : false,
      };
   });
});

const isAnswerValid = computed(() => {
   // There is no validation for this component except for (possibly) required
   if (props.block.isRequired && !state.sex) return false;
   else return true;
});

const errorList = computed(() => {
   if (!isAnswerValid.value)
      return [
         {
            inputId: props.block.id,
            validator: "required",
            message: $t("validation.choice.required"),
         },
      ];
   return [];
});

// Methods
// -----------------------------------------
/*
   Profile Block
   If the UserBlockAnswer answer is NULL, show loader (no inputs) and try to fetch any user profile data (e.g. another flow answer) to populate fields
   If the block answer is NOT NULL, use this to try to populate fields
*/
/**
 * Check userBlockAnswer for a previous response before
 * attempting to use user data to pre-populate
 */
function checkUserBlockAnswer() {
   if (props.userBlockAnswer) {
      const sex = props.userBlockAnswer.data?.sex;
      initialiseComponent(sex);
   } else {
      state.isFetchingData = true;
      fetchUserData().finally(() => (state.isFetchingData = false));
   }
}
/**
 * Try and fetch user data that we might be able to use to pre-populate fields with
 */
async function fetchUserData() {
   try {
      await userStore.setUser();
      initialiseComponent(userStore.user?.sex);
   } catch (e) {
      console.error("Failed to fetch user data", e);
   }
}

function initialiseComponent(sex) {
   const optionAnswers = choices.value.map((choice) => choice.id);
   if (sex && optionAnswers.includes(sex)) {
      state.sex = sex;
      const restoredChoice = choices.value.find((choice) => choice.id === sex);
      updateChoices(restoredChoice); // trigger as if user had made a selection
   }

   processInputs();
}

function updateChoices(userChoice) {
   state.sex = userChoice.id;
   processInputs();
}

function processInputs() {
   if (!isAnswerValid.value) {
      // an input is invalid or has errors
      emit("update-valid", false);
   } else {
      // all inputs are valid. This may be because there is a valid answer, or no answer
      // when this question is not required. For no answer, a different data format is required by the BE.
      const answerToSubmit = state.sex ? { sex: state.sex } : null;
      emit("update-valid", true);
      emit("update-answer", answerToSubmit);
   }
}

// execute on created
checkUserBlockAnswer();
</script>
